import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import DateUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/DateUtils";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { ProcessingState } from "@/models/data-room/ProcessingState";
import { DataRoomEntry, DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import DataRoomService from "@/services/DataRoomService";
import { extractExtension, extractExtensionRemoveDot } from "@/utils/FilenameUtils";
import FileWebSocketClient from "@/utils/websocket/FileWebSocketClient";
import { MessageType, WSFileStateChangeResponse } from "@/utils/websocket/filews/WSFileUploadTypes";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Collapse, MenuItem, Typography, buttonBaseClasses, styled } from "@mui/material";
import { RowSelectionState, SortDirection } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilePreview from "../common/FilePreview";
import { LazoCheckbox } from "../common/form/LazoCheckbox";
import CommonButton from "../utils/CommonButton";
import { FileTableMenu, FileTableMoreHoriz } from "../utils/FileTable/FileTableStyles";
import LazoModal from "../utils/LazoModal";
import { SimpleStarIcon } from "../utils/icons/SimpleStarIcon";
import DataRoomAISummary from "./DataRoomAISummary";
import { getSuggestedFolderValue } from "./utils";

type SortField = "name" | "createdAt";

const EXT_COLORS: Record<string, { bg: string; color: string }> = {
  PDF: { bg: "#FFEAEA", color: Colors.HEART_RED },
  DOCX: { bg: "#EEF0FF", color: Colors.BLUE_4763F4 },
  DOC: { bg: "#EEF0FF", color: Colors.BLUE_4763F4 },
  XLSX: { bg: "#EDFFF0", color: Colors.GREEN_01A902 },
  XLS: { bg: "#EDFFF0", color: Colors.GREEN_01A902 },
  PPTX: { bg: "#FFF4E5", color: Colors.ORANGE_FF9A3D },
  PPT: { bg: "#FFF4E5", color: Colors.ORANGE_FF9A3D },
};

function getExtColor(ext: string) {
  return EXT_COLORS[ext.toUpperCase()] ?? { bg: Colors.LIGHT_GRAY_DEDEE1, color: Colors.LIGHT_GRAY_8A8E93 };
}

type Props = {
  files: DataRoomFile[];
  setFiles: Dispatch<SetStateAction<DataRoomFile[]>>;
  selectedFiles: DataRoomFile[];
  setSelected: (v: DataRoomFile[]) => void;
  currentEntry: DataRoomEntry;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  onAskAI?: (file: DataRoomFile) => void;
  focusedFile?: DataRoomFile;
  setFocusedFile?: Dispatch<SetStateAction<DataRoomFile>>;
};

const fws = FileWebSocketClient;

function DataRoomMasterFilesTable(props: Props) {
  const { t } = useTranslation("virtualDataRooms");
  const [menuFile, setMenuFile] = useState<null | DataRoomFile>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [openConfirmDownloadModal, setOpenConfirmDownloadModal] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const user = AuthService.getUser();
  useMemo(() => {
    fws.subscribeRaw(user.externalId, (m) => {
      const d: WSFileStateChangeResponse = JSON.parse(m.body);
      if (d.content.messageType !== MessageType.STATE_CHANGED) return;
      props.setFiles((prev) =>
        prev.map((f) => {
          if (f.externalId === d.content.externalId) {
            if (d.content.fileState === ProcessingState.PROCESSED_SUGGESTED_FOLDER) {
              return { ...f, processingState: d.content.fileState, suggestedFolder: d.content.data, suggestedStaticId: d.content.data };
            } else if (
              d.content.fileState === ProcessingState.PROCESSED_AND_NOT_PLACED ||
              d.content.fileState === ProcessingState.PROCESSED_PLACED_NOT_SEEN
            ) {
              return { ...f, processingState: d.content.fileState, aiAnalysis: d.content.data };
            }
            return { ...f, processingState: d.content.fileState };
          }
          return f;
        })
      );
    });
  }, []);

  // Sorting
  const sortedFiles = useMemo(() => {
    return [...props.files].sort((a, b) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [props.files, sortField, sortDir]);

  function handleSortClick(field: SortField) {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  // Deselect files that are processing
  useEffect(() => {
    props.files.forEach((file, idx) => {
      if (file.processingState === ProcessingState.PROCESSING_SUGGESTED_FOLDER && props.rowSelection[idx]) {
        props.setRowSelection((prev) => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });
      }
    });
  }, [props.files]);

  // Selection
  const allSelected = props.files.length > 0 && props.files.every((_, idx) => props.rowSelection[idx]);
  const someSelected = !allSelected && props.files.some((_, idx) => props.rowSelection[idx]);

  function handleSelectAll(checked: boolean) {
    if (checked) {
      const all: RowSelectionState = {};
      props.files.forEach((file, idx) => {
        if (file.processingState !== ProcessingState.PROCESSING_SUGGESTED_FOLDER) all[idx] = true;
      });
      props.setRowSelection(all);
    } else {
      props.setRowSelection({});
    }
  }

  function handleCheckFile(checked: boolean, originalIdx: number) {
    props.setRowSelection((prev) => {
      const next = { ...prev };
      if (checked) next[originalIdx] = true;
      else delete next[originalIdx];
      return next;
    });
  }

  // Presigned URLs for expanded rows
  const [preSignedMap, setPreSignedMap] = useState<{ [externalId: string]: string }>({});
  useEffect(() => {
    Object.keys(expanded).forEach((k) => {
      const file = props.files[Number(k)];
      if (expanded[k] && file && !preSignedMap[file.externalId]) {
        DataRoomService.getPreSignedUrl(file.externalId).then((r) => {
          r.onSuccess((u) => {
            setPreSignedMap((prev) => ({ ...prev, [file.externalId]: u }));
          });
        });
      }
    });
  }, [expanded]);

  function toggleExpanded(originalIdx: number) {
    setExpanded((prev) => ({ ...prev, [originalIdx]: !prev[originalIdx] }));
  }

  function handleMoreClick(ev: React.MouseEvent<HTMLElement>, file: DataRoomFile) {
    setMenuAnchor(ev.currentTarget);
    setMenuFile(file);
  }

  function handleMenuClose() {
    setMenuAnchor(null);
    setMenuFile(null);
  }

  function downloadFiles() {
    setDownloading(true);
    const fExternalIds = props.selectedFiles?.length === 0 ? [menuFile.externalId] : props.selectedFiles.map((f) => f.externalId);
    let filename = props.currentEntry.name;
    if (fExternalIds.length === 1) filename = menuFile.name;
    DataRoomService.downloadFiles(fExternalIds, filename)
      .then(() => setOpenConfirmDownloadModal(false))
      .catch((e) => {
        NotificationService.error(t("masterFilesTable.notifications.downloadError"));
        console.log("Error: ", +e);
      })
      .finally(() => setDownloading(false));
  }

  function handleDownloadClick() {
    if (props.selectedFiles?.length > 1) setOpenConfirmDownloadModal(true);
    else downloadFiles();
  }

  function deleteFiles(): void {
    setDeleting(true);
    const fExternalIds = props.selectedFiles?.length === 0 ? [menuFile.externalId] : props.selectedFiles.map((f) => f.externalId);
    DataRoomService.deleteFiles(fExternalIds)
      .then((r) => {
        r.onSuccess((_) => {
          NotificationService.info(t("masterFilesTable.notifications.fileDeleted"));
          props.setFiles((prev) => prev.filter((f) => !fExternalIds.includes(f.externalId)));
          props.setSelected([]);
          props.setRowSelection({});
          setOpenConfirmDeleteModal(false);
        });
        r.onError((e) => {
          NotificationService.error(t("masterFilesTable.notifications.deleteError"));
          console.log("error: " + e);
        });
      })
      .finally(() => setDeleting(false));
  }

  function handleFileUpdate(u: DataRoomFile) {
    props.setFiles((prev) => prev.map((f) => (f.externalId === u.externalId ? u : f)));
  }

  function handleAskAIClick() {
    props.onAskAI?.(menuFile);
    handleMenuClose();
  }

  return (
    <>
      <FileListWrapper>
        {/* Header sticky */}
        <FileListHeader>
          <StyledLazoCheckbox checked={allSelected} indeterminate={someSelected} onChange={(e) => handleSelectAll(e.target.checked)} />
          <HeaderLabel onClick={() => handleSortClick("name")}>
            {t("masterFilesTable.headers.name")}
            {sortField === "name" && (sortDir === "asc" ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />)}
          </HeaderLabel>
          <HeaderLabel>{t("masterFilesTable.headers.suggestedFolder")}</HeaderLabel>
          <HeaderLabel onClick={() => handleSortClick("createdAt")}>
            {t("masterFilesTable.headers.dateUploaded")}
            {sortField === "createdAt" &&
              (sortDir === "asc" ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />)}
          </HeaderLabel>
          <Box /> {/* actions column */}
        </FileListHeader>

        {/* Cards */}
        {sortedFiles.map((file) => {
          const originalIdx = props.files.indexOf(file);
          const isSelected = !!props.rowSelection[originalIdx];
          const isFocused = props.focusedFile?.externalId === file.externalId;
          const isExpanded = !!expanded[originalIdx];
          const canSelect = file.processingState !== ProcessingState.PROCESSING_SUGGESTED_FOLDER;
          const ext = extractExtension(file.name.toUpperCase()).slice(1);
          const extColors = getExtColor(ext);

          return (
            <MasterFileCardWrapper key={file.externalId}>
              <FileCard isSelected={isSelected} isFocused={isFocused} onMouseOver={() => isFocused && props.setFocusedFile?.(null)}>
                <Box sx={{ flexShrink: 0, width: "1.5rem", display: "flex", alignItems: "center" }}>
                  <StyledLazoCheckbox
                    checked={isSelected}
                    disabled={!canSelect}
                    onChange={(e) => handleCheckFile(e.target.checked, originalIdx)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
                  <ExtBadge extbg={extColors.bg} extcolor={extColors.color}>
                    {ext}
                  </ExtBadge>
                  <FileNameTypography>{file.name}</FileNameTypography>
                </Box>
                <SuggestedFolderTypography>{getSuggestedFolderValue(file)}</SuggestedFolderTypography>
                <DateTypography>{DateUtils.formatDate(new Date(file.createdAt), "MM/DD/YYYY")}</DateTypography>
                <Box className={isExpanded ? undefined : "row-actions"} sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                  <Box onClick={() => toggleExpanded(originalIdx)}>
                    <AIAnalysisButton open={isExpanded} />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }} onClick={(ev) => handleMoreClick(ev, file)}>
                    <FileTableMoreHoriz />
                  </Box>
                </Box>
              </FileCard>

              <Collapse in={isExpanded} unmountOnExit>
                <AIExpandedPanel>
                  {preSignedMap[file.externalId] ? (
                    <FilePreview
                      isPreviewModal={true}
                      fileUrl={preSignedMap[file.externalId]}
                      fileType={extractExtensionRemoveDot(file.name)}
                      viewportWidth="28.5rem"
                      viewportHeight="26rem"
                    />
                  ) : (
                    <Box display="flex" width="28.5rem" justifyContent="center" alignItems="center">
                      <Spinner size="2rem" sx={{ color: Colors.LAZO_PURPLE }} />
                    </Box>
                  )}
                  <DataRoomAISummary
                    updateFn={(f, updated) => DataRoomService.updateSummary(f, updated)}
                    file={file}
                    setFile={handleFileUpdate}
                  />
                </AIExpandedPanel>
              </Collapse>
            </MasterFileCardWrapper>
          );
        })}
      </FileListWrapper>

      <FileTableMenu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose} transitionDuration={0}>
        <MenuItem disabled={downloading} onClick={handleDownloadClick}>
          <Typography>{t("masterFilesTable.menu.download")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleAskAIClick}>
          <Typography>{t("masterFilesTable.menu.askAI")}</Typography>
        </MenuItem>
        <MenuItem disabled={deleting} onClick={() => setOpenConfirmDeleteModal(true)}>
          <Typography color="red">{t("masterFilesTable.menu.delete")}</Typography>
        </MenuItem>
      </FileTableMenu>

      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openConfirmDownloadModal}
        title={t("masterFilesTable.modals.confirmDownload.title")}
        handleClose={() => setOpenConfirmDownloadModal(false)}
        buttons={[
          <CommonButton
            style={{ borderRadius: "0.5rem" }}
            purpleBorderVariant
            onClick={() => setOpenConfirmDownloadModal(false)}
            key="cancel-button"
          >
            {t("masterFilesTable.modals.confirmDownload.buttons.cancel")}
          </CommonButton>,
          <CommonButton style={{ borderRadius: "0.5rem" }} onClick={downloadFiles} disabled={downloading} key="download-button">
            {!downloading ? t("masterFilesTable.modals.confirmDownload.buttons.download") : <Spinner size="1.5rem" />}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          {t("masterFilesTable.modals.confirmDownload.message", { count: props.selectedFiles.length })}
        </Typography>
      </LazoModal>

      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openConfirmDeleteModal}
        title={t("masterFilesTable.modals.confirmDelete.title")}
        handleClose={() => setOpenConfirmDeleteModal(false)}
        buttons={[
          <CommonButton
            style={{ borderRadius: "0.5rem" }}
            purpleBorderVariant
            onClick={() => setOpenConfirmDeleteModal(false)}
            key="delete-cancel-button"
          >
            {t("masterFilesTable.modals.confirmDelete.buttons.cancel")}
          </CommonButton>,
          <CommonButton
            style={{ borderRadius: "0.5rem", backgroundColor: Colors.HEART_RED }}
            onClick={deleteFiles}
            disabled={deleting}
            key="delete-confirm-button"
          >
            {!deleting ? t("masterFilesTable.modals.confirmDelete.buttons.delete") : <Spinner size="1.5rem" />}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          {props.selectedFiles?.length > 1
            ? t("masterFilesTable.modals.confirmDelete.messageMultiple", { count: props.selectedFiles.length })
            : t("masterFilesTable.modals.confirmDelete.messageSingle")}
        </Typography>
      </LazoModal>
    </>
  );
}

function AIAnalysisButton({ open }: { open: boolean }) {
  const { t } = useTranslation("virtualDataRooms");
  return (
    <AIAnalysisTypography>
      <SimpleStarIcon style={{ fill: Colors.BUTTON_PURPLE }} />
      {t("masterFilesTable.actions.aiAnalysis")}
      {!open ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
    </AIAnalysisTypography>
  );
}

// ─── Styled Components ────────────────────────────────────────────────────────

const FileListWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  overflowY: "auto",
});

// Columns: checkbox | name+badge | suggested folder | date | actions
const GRID_COLUMNS = "1.5rem 1fr 1fr 7rem 10rem";

const FileListHeader = styled(Box)({
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  alignItems: "center",
  gap: "0.75rem",
  padding: "0.5rem 1rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderBottom: `1px solid rgba(0,0,0,0.06)`,
  position: "sticky",
  top: 0,
  zIndex: 1,
  flexShrink: 0,
});

const HeaderLabel = styled(Typography)({
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: Colors.LIGHT_GRAY_8A8E93,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "0.2rem",
  userSelect: "none",
  "&:hover": { color: Colors.BLACK_151517 },
});

const MasterFileCardWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const FileCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isFocused",
})<{ isSelected: boolean; isFocused: boolean }>(({ isSelected, isFocused }) => ({
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  alignItems: "center",
  gap: "0.75rem",
  padding: "0 1rem",
  height: "3.5rem",
  borderBottom: `1px solid rgba(0,0,0,0.06)`,
  backgroundColor: isFocused ? Colors.LIGHT_GREEN_DESATURATED_FCFFEC : isSelected ? Colors.BUTTON_SECONDARY_HOVER_PURPLE : "transparent",
  transition: "background-color 0.15s ease",
  "& .row-actions": {
    opacity: 0,
    transition: "opacity 0.15s ease",
  },
  "&:hover": {
    backgroundColor: isSelected ? Colors.BUTTON_SECONDARY_HOVER_PURPLE : Colors.LIGHT_GRAY_BG_F6F8FA,
    "& .row-actions": {
      opacity: 1,
    },
  },
}));

const ExtBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "extbg" && prop !== "extcolor",
})<{ extbg: string; extcolor: string }>(({ extbg, extcolor }) => ({
  backgroundColor: extbg,
  color: extcolor,
  borderRadius: "0.3rem",
  padding: "0.15rem 0.4rem",
  fontSize: "0.625rem",
  fontWeight: 700,
  lineHeight: "1rem",
  textAlign: "center",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
}));

const FileNameTypography = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: "20px",
  color: Colors.BLACK_151517,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const SuggestedFolderTypography = styled(Typography)({
  fontSize: "0.8125rem",
  fontWeight: 400,
  color: Colors.LAZO_PURPLE,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "& .MuiTypography-root": {
    fontSize: "0.8125rem",
    color: Colors.LAZO_PURPLE,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const DateTypography = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 400,
  color: Colors.LIGHT_GRAY_8A8E93,
});

const AIExpandedPanel = styled(Box)({
  display: "flex",
  gap: "1.5rem",
  width: "100%",
  padding: "1.25rem 1rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderTop: `1px solid rgba(0,0,0,0.06)`,
});

const AIAnalysisTypography = styled(Typography)(() => ({
  display: "flex",
  gap: "0.25rem",
  justifyContent: "center",
  alignItems: "center",
  whiteSpace: "nowrap",
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: "18px",
  color: Colors.BUTTON_PURPLE,
  width: "fit-content",
  cursor: "pointer",
  flexShrink: 0,
  borderRadius: "0.25rem",
  padding: "0.25rem 0.5rem",
  transition: "background-color 0.15s ease",
  ":hover": {
    backgroundColor: `${Colors.BUTTON_PURPLE}14`,
  },
}));

const StyledLazoCheckbox = styled(LazoCheckbox)(() => ({
  [`&.${buttonBaseClasses.root}`]: { padding: 0 },
}));

export default DataRoomMasterFilesTable;
